/**
 * Week-grid helpers shared by the web app and the API: the half-hour cell grid used to show and edit
 * a trainer's availability, conversion to/from time-window slots, and the plain-words summary.
 */
import type { Weekday } from "./index.ts";

export const DAYS: Weekday[] = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];
export const DAY_SHORT: Record<Weekday, string> = { MON: "Mon", TUE: "Tue", WED: "Wed", THU: "Thu", FRI: "Fri", SAT: "Sat", SUN: "Sun" };

/** Grid covers 05:00–22:00 in 30-minute cells. */
export const GRID_START_MIN = 5 * 60;
export const GRID_END_MIN = 22 * 60;
export const CELL_MIN = 30;
export const CELLS_PER_DAY = (GRID_END_MIN - GRID_START_MIN) / CELL_MIN;

/** Hard-coded UK defaults for vague words. Always stated back to the trainer when used. */
export const UK_DEFAULTS = {
  morning: { startTime: "06:00", endTime: "12:00" },
  afternoon: { startTime: "12:00", endTime: "17:00" },
  evening: { startTime: "17:00", endTime: "21:00" },
  allDay: { startTime: "06:00", endTime: "21:00" },
} as const;

export interface Window {
  weekday: Weekday;
  startTime: string;
  endTime: string;
}

export type Cells = Record<Weekday, boolean[]>;

export const toMin = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
export const toHHMM = (m: number) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;

export function emptyCells(): Cells {
  return Object.fromEntries(DAYS.map((d) => [d, Array<boolean>(CELLS_PER_DAY).fill(false)])) as Cells;
}

export function cloneCells(c: Cells): Cells {
  return Object.fromEntries(DAYS.map((d) => [d, [...c[d]]])) as Cells;
}

/** A cell is on if the slot covers at least half of it (so 06:15–07:00 still shows 06:00–06:30). */
export function slotsToCells(slots: { weekday: string; startTime: string; endTime: string }[]): Cells {
  const cells = emptyCells();
  for (const s of slots) {
    const d = s.weekday as Weekday;
    if (!cells[d]) continue;
    const a = toMin(s.startTime);
    const b = toMin(s.endTime);
    for (let i = 0; i < CELLS_PER_DAY; i++) {
      const cs = GRID_START_MIN + i * CELL_MIN;
      const overlap = Math.min(b, cs + CELL_MIN) - Math.max(a, cs);
      if (overlap >= CELL_MIN / 2) cells[d][i] = true;
    }
  }
  return cells;
}

export function cellsToSlots(cells: Cells): Window[] {
  const out: Window[] = [];
  for (const d of DAYS) {
    let run: number | null = null;
    const row = cells[d];
    for (let i = 0; i <= CELLS_PER_DAY; i++) {
      const on = i < CELLS_PER_DAY && row[i];
      if (on && run === null) run = i;
      if (!on && run !== null) {
        out.push({ weekday: d, startTime: toHHMM(GRID_START_MIN + run * CELL_MIN), endTime: toHHMM(GRID_START_MIN + i * CELL_MIN) });
        run = null;
      }
    }
  }
  return out;
}

/** Merge touching/overlapping windows per day and sort. */
export function normaliseSlots(slots: { weekday: string; startTime: string; endTime: string }[]): Window[] {
  const out: Window[] = [];
  for (const d of DAYS) {
    const day = slots.filter((s) => s.weekday === d).map((s) => [toMin(s.startTime), toMin(s.endTime)] as [number, number]).filter(([a, b]) => b > a).sort((x, y) => x[0] - y[0]);
    const merged: [number, number][] = [];
    for (const w of day) {
      const last = merged.at(-1);
      if (last && w[0] <= last[1]) last[1] = Math.max(last[1], w[1]);
      else merged.push([...w]);
    }
    for (const [a, b] of merged) out.push({ weekday: d, startTime: toHHMM(a), endTime: toHHMM(b) });
  }
  return out;
}

export function cellsChanged(a: Cells, b: Cells): number {
  let n = 0;
  for (const d of DAYS) for (let i = 0; i < CELLS_PER_DAY; i++) if (a[d][i] !== b[d][i]) n++;
  return n;
}

export function totalHours(slots: { startTime: string; endTime: string }[]): number {
  return slots.reduce((a, s) => a + (toMin(s.endTime) - toMin(s.startTime)) / 60, 0);
}

/** "Mon–Fri", "Mon, Wed, Fri", "Sat & Sun" */
export function describeDays(days: Weekday[]): string {
  const idx = [...new Set(days)].map((d) => DAYS.indexOf(d)).filter((i) => i >= 0).sort((a, b) => a - b);
  if (idx.length === 7) return "every day";
  const runs: [number, number][] = [];
  for (const i of idx) {
    const last = runs.at(-1);
    if (last && i === last[1] + 1) last[1] = i;
    else runs.push([i, i]);
  }
  const parts = runs.flatMap(([a, b]) => (b - a >= 2 ? [`${DAY_SHORT[DAYS[a]!]}–${DAY_SHORT[DAYS[b]!]}`] : b === a ? [DAY_SHORT[DAYS[a]!]] : [DAY_SHORT[DAYS[a]!], DAY_SHORT[DAYS[b]!]]));
  return parts.length === 2 ? parts.join(" & ") : parts.join(", ");
}

/** "6am", "12:30pm" */
export function clock12(t: string): string {
  const m = toMin(t);
  const h = Math.floor(m / 60) % 24;
  const mm = m % 60;
  const suffix = h < 12 ? "am" : "pm";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}${mm ? `:${String(mm).padStart(2, "0")}` : ""}${suffix}`;
}

/**
 * The interpretation in words, grouping days that share identical hours:
 *   "Mon–Fri 06:00–11:00 and 16:00–20:00, Sat 08:00–12:00"
 * style "12h" gives "Mon–Fri, 6am–12pm" (used for the running summary under the grid).
 */
export function describeWeek(slots: { weekday: string; startTime: string; endTime: string }[], style: "24h" | "12h" = "24h"): string {
  const norm = normaliseSlots(slots);
  if (!norm.length) return "No hours set";
  const fmt = (w: Window) => (style === "12h" ? `${clock12(w.startTime)}–${clock12(w.endTime)}` : `${w.startTime}–${w.endTime}`);
  const keyOf = (d: Weekday) => norm.filter((w) => w.weekday === d).map(fmt).join(" and ");
  const groups: { key: string; days: Weekday[] }[] = [];
  for (const d of DAYS) {
    const key = keyOf(d);
    if (!key) continue;
    const g = groups.find((x) => x.key === key);
    if (g) g.days.push(d);
    else groups.push({ key, days: [d] });
  }
  const sep = style === "12h" ? ", " : " ";
  return groups.map((g) => `${capitalise(describeDays(g.days))}${sep}${g.key}`).join(style === "12h" ? " · " : ", ");
}

/** "Mon–Fri, 6am–12pm · 30 hrs available" */
export function runningSummary(slots: { weekday: string; startTime: string; endTime: string }[]): string {
  const h = totalHours(normaliseSlots(slots));
  if (!h) return "No hours yet";
  const hrs = Number.isInteger(h) ? String(h) : h.toFixed(1);
  return `${describeWeek(slots, "12h")} · ${hrs} hrs available`;
}

const capitalise = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
